import React, { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";
import { Download } from "lucide-react";
import { toast } from "sonner";

const MONTHS = ["Gen", "Feb", "Mar", "Apr", "Mag", "Giu", "Lug", "Ago", "Set", "Ott", "Nov", "Dic"];
const API_BASE = process.env.REACT_APP_BACKEND_URL;

function euro(n) { return new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(n || 0); }

export default function Report() {
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(String(currentYear));
  const [tabKind, setTabKind] = useState("uscita");

  const { data } = useQuery({
    queryKey: ["admin-report", year],
    queryFn: async () => (await api.get("/administration/report/monthly", { params: { year: parseInt(year, 10) } })).data,
  });

  const chartData = useMemo(() => (data?.monthly || []).map((m) => ({
    name: MONTHS[m.month - 1], Entrate: m.entrata, Uscite: m.uscita, Saldo: m.entrata - m.uscita,
  })), [data]);

  const pieData = useMemo(() => (data?.by_category || []).filter((c) => c.type === tabKind).slice(0, 12), [data, tabKind]);

  const yearTotals = useMemo(() => {
    const inc = (data?.monthly || []).reduce((s, m) => s + m.entrata, 0);
    const out = (data?.monthly || []).reduce((s, m) => s + m.uscita, 0);
    return { inc, out, saldo: inc - out };
  }, [data]);

  const downloadExcel = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API_BASE}/api/administration/report/export.xlsx?date_from=${year}-01-01&date_to=${year}-12-31`, { headers: { Authorization: `Bearer ${token}` } });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = `movimenti_${year}.xlsx`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Errore export"); }
  };

  return (
    <div className="p-6 max-w-[1400px] mx-auto space-y-5" data-testid="report-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-[#1C352D]">Report</h1>
          <p className="text-sm text-stone-500 mt-1">Andamento mensile e distribuzione per categoria.</p>
        </div>
        <div className="flex gap-2 items-center">
          <Select value={year} onValueChange={setYear}>
            <SelectTrigger className="w-28" data-testid="year-select"><SelectValue/></SelectTrigger>
            <SelectContent>
              {Array.from({ length: 5 }, (_, i) => currentYear - i).map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={downloadExcel} data-testid="report-xlsx"><Download size={14} className="mr-1"/> Excel {year}</Button>
        </div>
      </div>

      {/* KPI */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="p-5 border border-stone-200 rounded-xl shadow-none">
          <div className="text-[10px] uppercase tracking-widest text-stone-500">Entrate {year}</div>
          <div className="text-3xl font-semibold text-emerald-700 mt-1">{euro(yearTotals.inc)}</div>
        </Card>
        <Card className="p-5 border border-stone-200 rounded-xl shadow-none">
          <div className="text-[10px] uppercase tracking-widest text-stone-500">Uscite {year}</div>
          <div className="text-3xl font-semibold text-red-700 mt-1">{euro(yearTotals.out)}</div>
        </Card>
        <Card className="p-5 border border-stone-200 rounded-xl shadow-none">
          <div className="text-[10px] uppercase tracking-widest text-stone-500">Saldo {year}</div>
          <div className={`text-3xl font-semibold mt-1 ${yearTotals.saldo >= 0 ? "text-[#1C352D]" : "text-red-700"}`}>{euro(yearTotals.saldo)}</div>
        </Card>
      </div>

      {/* Bar chart */}
      <Card className="p-5 border border-stone-200 rounded-xl shadow-none">
        <div className="text-sm font-medium mb-4">Entrate vs Uscite (mensile)</div>
        <div style={{ width: "100%", height: 320 }}>
          <ResponsiveContainer>
            <BarChart data={chartData}>
              <XAxis dataKey="name" tick={{ fontSize: 11 }}/>
              <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `€${v}`}/>
              <Tooltip formatter={(v) => euro(v)}/>
              <Legend wrapperStyle={{ fontSize: 12 }}/>
              <Bar dataKey="Entrate" fill="#16a34a" radius={[6, 6, 0, 0]}/>
              <Bar dataKey="Uscite" fill="#dc2626" radius={[6, 6, 0, 0]}/>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card>

      {/* Pie categorie */}
      <Card className="border border-stone-200 rounded-xl shadow-none">
        <div className="p-5 border-b border-stone-100 flex items-center justify-between">
          <div className="text-sm font-medium">Distribuzione per categoria</div>
          <div className="flex gap-2">
            <button onClick={() => setTabKind("entrata")} className={`px-3 py-1 rounded-full text-xs ${tabKind === "entrata" ? "bg-emerald-100 text-emerald-800" : "bg-stone-100 text-stone-500"}`} data-testid="pie-tab-in">Entrate</button>
            <button onClick={() => setTabKind("uscita")} className={`px-3 py-1 rounded-full text-xs ${tabKind === "uscita" ? "bg-red-100 text-red-800" : "bg-stone-100 text-stone-500"}`} data-testid="pie-tab-out">Uscite</button>
          </div>
        </div>
        <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4 items-center">
          <div style={{ width: "100%", height: 300 }}>
            {pieData.length === 0 ? (
              <div className="h-full flex items-center justify-center text-sm text-stone-400">Nessun dato per {tabKind}</div>
            ) : (
              <ResponsiveContainer>
                <PieChart>
                  <Pie data={pieData} dataKey="total" nameKey="category_name" cx="50%" cy="50%" outerRadius={100} innerRadius={55} paddingAngle={2}>
                    {pieData.map((entry, i) => <Cell key={i} fill={entry.color || "#94a3b8"}/>)}
                  </Pie>
                  <Tooltip formatter={(v) => euro(v)}/>
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
          <ul className="space-y-1.5">
            {pieData.map((c) => (
              <li key={c.category_id} className="flex items-center gap-2 text-xs">
                <span className="w-3 h-3 rounded-sm" style={{ background: c.color || "#94a3b8" }}/>
                <span className="flex-1 truncate">{c.category_name}</span>
                <span className="font-medium text-stone-700">{euro(c.total)}</span>
              </li>
            ))}
          </ul>
        </div>
      </Card>
    </div>
  );
}
